// Base URL for API
const baseURL = '/api/inbound-trucks';

// Helper function to create headers with authentication
const getHeaders = () => {
  const headers = {
    'Content-Type': 'application/json'
  };
  
  const token = localStorage.getItem('token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  return headers;
};

// Enhanced error handler for better debugging
const handleError = async (error, response) => {
  console.error('API Error:', error);

  // Try to extract detailed error information from the response if available
  let errorMessage = error.message || 'An error occurred';
  let errorDetails = {};

  if (response) {
    try {
      // Try to parse the response body as JSON
      const errorData = await response.json();
      console.error('Server error response:', errorData);

      // Extract more detailed error information
      errorMessage = errorData.message || errorMessage;
      errorDetails = errorData;
    } catch (jsonError) {
      console.error('Failed to parse error response as JSON:', jsonError);
    }
  }

  if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
    return Promise.reject('Network error. Please check your connection.');
  } else if (typeof error === 'string') {
    return Promise.reject(error);
  } else {
    return Promise.reject({
      message: errorMessage,
      details: errorDetails,
      status: response?.status
    });
  }
};

const inboundTruckService = {
  // Get all trucks with optional filtering
  getAllTrucks: async (filters = {}) => {
    try {
      // Convert filters object to query string
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        queryParams.append(key, value);
      });
      
      const queryString = queryParams.toString();
      const url = `${baseURL}/${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },

  // Get truck by ID
  getTruckById: async (id) => {
    try {
      const response = await fetch(`${baseURL}/${id}`, {
        method: 'GET',
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },

  // Create new truck schedule
  createTruck: async (truckData) => {
    try {
      const response = await fetch(baseURL, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(truckData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },

  // Update truck schedule
  updateTruck: async (id, truckData) => {
    let response;
    try {
      console.log(`updateTruck - Sending data to server:`, truckData);

      response = await fetch(`${baseURL}/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(truckData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      return handleError(error, response);
    }
  },

  // Delete truck schedule
  deleteTruck: async (id) => {
    try {
      const response = await fetch(`${baseURL}/${id}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },

  // Mark truck as arrived
  markTruckAsArrived: async (id, arrivalData) => {
    try {
      const response = await fetch(`${baseURL}/${id}/arrive`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(arrivalData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },

  // Start receiving process
  startReceiving: async (id) => {
    try {
      const response = await fetch(`${baseURL}/${id}/start-receiving`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },

  // Complete receiving process
  completeReceiving: async (id, receivingData) => {
    try {
      const response = await fetch(`${baseURL}/${id}/complete-receiving`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(receivingData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },

  // Get truck statistics
  getTruckStats: async () => {
    try {
      const response = await fetch(`${baseURL}/stats`, {
        method: 'GET',
        headers: getHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },

  // Complete putaway process - dedicated endpoint for completing a truck
  completePutaway: async (id, completionData = {}) => {
    try {
      const response = await fetch(`${baseURL}/${id}/complete-putaway`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(completionData || {})
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },

  // Update truck status with appropriate timestamps
  updateTruckStatus: async (id, status, additionalData = {}) => {
    try {
      const statusData = {
        ...additionalData
      };

      // Add appropriate timestamp fields based on status (if not already provided)
      switch (status) {
        case 'Arrived':
          if (!statusData.arrived_at) {
            statusData.arrived_at = new Date().toISOString();
          }
          // Use the dedicated arrived endpoint instead
          return await inboundTruckService.markTruckAsArrived(id, {
            arrived_at: statusData.arrived_at
          });

        case 'Received':
          if (!statusData.received_at) {
            statusData.received_at = new Date().toISOString();
          }
          // Use the dedicated receiving endpoint
          return await inboundTruckService.startReceiving(id);

        case 'Putaway':
          if (!statusData.putaway_at) {
            statusData.putaway_at = new Date().toISOString();
          }
          // Use the dedicated completing endpoint with any additional data
          return await inboundTruckService.completeReceiving(id, {
            conforming: statusData.conforming !== undefined ? statusData.conforming : true
          });

        case 'Completed':
          // Use dedicated endpoint for completion
          if (!statusData.completed_at) {
            statusData.completed_at = new Date().toISOString();
          }
          console.log('Using dedicated completePutaway endpoint for Completed status');
          return await inboundTruckService.completePutaway(id, statusData);

        case 'Cancelled':
          if (!statusData.cancelled_at) {
            statusData.cancelled_at = new Date().toISOString();
          }
          break;

        default:
          break;
      }

      // Log what we're sending for debugging
      console.log(`updateTruckStatus - Truck ID: ${id}, Status: ${status}`);
      console.log(`updateTruckStatus - Payload:`, statusData);

      // Use the updateTruck method to send the request
      return await inboundTruckService.updateTruck(id, statusData);
    } catch (error) {
      console.error(`Error in updateTruckStatus for ID ${id} to status ${status}:`, error);
      return handleError(error);
    }
  }
};

export default inboundTruckService;